import { type ChangeEvent, type FC, useState } from "react";
import Modal from "../../../common/components/Modal";
import { LINEAR_REQUEST_QUOTE_EMAIL, LinearPopupState } from "../constants";
import { getCurrentStep, getProductCode } from "../redux/selectors";
import { useDispatch, useSelector } from "react-redux";
import { Stacker } from "~/common/components/Stacker/Stacker";
import { CloseBtn } from "../../../AddToCart/components/addToCart/AtcCloseButton";
import { Col, Row } from "../../../common/components/Layout";
import { sendLinearRequestQuote } from "../api/sendLinearRequestQuote";
import { setAlreadyAddedToCart } from "../redux/actions";
import { Button } from "~/common/components/Button/Button";
import { useTranslation } from "react-i18next";
import { useLinearProductInfo } from "~/Configurators/Linear/redux/useLinearProductInfo";
import { TextField } from "@mui/material";
import { Alert } from "~/common/m3/Alert/Alert";

interface Props {
  onClose: () => void;
}

const LinearRequestQuotePopup: FC<Props> = (props: Props) => {
  const { onClose } = props;
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const [state, setState] = useState(LinearPopupState.INITIAL);
  const [recipientNote, setRecipientNote] = useState<string>("");
  const updateRecipientNote = (e: ChangeEvent<HTMLTextAreaElement>) => setRecipientNote(e.target.value);

  const productInfo = useLinearProductInfo();
  const productCode = useSelector(getProductCode);
  const currentStep = useSelector(getCurrentStep);

  const isInState = (...validStates: LinearPopupState[]) => {
    const result = validStates.find((el) => el === state);
    return result !== undefined;
  };

  const sendQuoteRequest = async () => {
    setState(LinearPopupState.SENDING);
    let success: boolean;
    try {
      success = await sendLinearRequestQuote(
        productCode,
        productInfo.configId,
        recipientNote,
        currentStep + 1,
        productInfo.designation,
      );
    } catch (e) {
      success = false;
    }
    if (success) {
      dispatch(setAlreadyAddedToCart(null));
    }
    setState(success ? LinearPopupState.SUCCESS : LinearPopupState.ERROR);
  };

  return (
    <Modal
      onClose={onClose}
      title={t("linearConfigSummary.card.addToQuote")}
      footerChildren={
        <Row className={"d-flex flex-justify-between flex-align-center w-100"}>
          <p>{t("linear.request.quote.popup.mandatory")}</p>
          <Stacker direction={"row"} gap={8}>
            {isInState(LinearPopupState.INITIAL, LinearPopupState.SENDING) ? (
              <CloseBtn onClick={onClose} disabled={isInState(LinearPopupState.SENDING)}>
                {t("cancel")}
              </CloseBtn>
            ) : (
              <CloseBtn onClick={onClose}>{t("close")}</CloseBtn>
            )}

            <Button
              disabled={isInState(LinearPopupState.SENDING, LinearPopupState.ERROR)}
              onClick={isInState(LinearPopupState.INITIAL) ? sendQuoteRequest : undefined}
            >
              {t("linearConfigSummary.card.addToQuote")}
            </Button>
          </Stacker>
        </Row>
      }
    >
      <div className={"mt-30"}>{t("linear.request.quote.popup.body")}</div>
      <p className={"scha__param__label mt-30"}>{t("linear.request.quote.popup.email.label")}</p>
      <div className={"mt-1"}>{LINEAR_REQUEST_QUOTE_EMAIL}</div>
      <div className={"mt-2"}>
        <TextField
          variant={"outlined"}
          multiline
          fullWidth
          inputProps={{ sx: { height: "200px !important" } }}
          label={t("linear.request.quote.popup.input.label")}
          onChange={updateRecipientNote}
        />
      </div>
      <Alert className={"mt-10 mb-30"} severity="info">
        {t("linear.request.quote.popup.email.info")}
      </Alert>
    </Modal>
  );
};

export default LinearRequestQuotePopup;
